// Inspection form schema + payload logic — direct port of the mobile
// BS 8214:2026 form (see /app/memory/web-app-snippets/INSPECTION_FORM_PORTING.md).
import { recommendGapRemedies } from "@/pdf/bs8214Recommendations";

// ── 1. The 19-question checklist (BS 8214 exact order) ───────────────
export const INITIAL_CHECKLIST = [
  { key: "door_operates_correctly", question: "Does the door look OK and operate correctly?", value: true, type: "switch" },
  { key: "door_frame_secure", question: "Is the door frame secure and without damage?", value: true, type: "switch" },
  { key: "door_label_present", question: "Is the door identification label present?", value: true, type: "switch" },
  { key: "smoke_fire_seals_intact", question: "Are all smoke & fire seals in place and intact?", value: true, type: "switch" },
  { key: "smoke_seal_test_pass", question: "Does it pass the smoke seal test?", value: true, type: "switch" },
  { key: "glass_intact", question: "Is the glass in place and not broken?", value: "Yes", type: "radio", options: ["Yes", "No", "N/A"] },
  { key: "window_beads_secure", question: "Window beads all present and secure?", value: "Yes", type: "radio", options: ["Yes", "No", "N/A"] },
  { key: "handle_lock_secure", question: "Handle and door lock secure and working?", value: true, type: "switch" },
  { key: "door_closer_working", question: "Door closer working?", value: "Yes", type: "radio", options: ["Yes", "No", "N/A"] },
  { key: "three_hinges_fitted", question: "Are three hinges fitted and working?", value: true, type: "switch" },
  { key: "door_to_stop_bead", question: "Is the door-to-stop bead in place and secure?", value: true, type: "switch" },
  { key: "signage_in_place", question: "Is all signage in place?", value: "Yes", type: "radio", options: ["Yes", "No", "N/A"] },
  { key: "push_plates_secure", question: "All push plates / kick plates in place and secure?", value: "Yes", type: "radio", options: ["Yes", "No", "N/A"] },
  { key: "dropdown_seal", question: "Dropdown seal intact and working?", value: "N/A", type: "radio", options: ["Yes", "No", "N/A"] },
  { key: "spy_viewer", question: "Spy viewer in place and secure?", value: "N/A", type: "radio", options: ["Yes", "No", "N/A"] },
  { key: "letterbox_present", question: "Does the door have a letterbox?", value: "No", type: "radio", options: ["Yes", "No", "N/A"], isGate: true },
  { key: "letterbox_fire_rated", question: "Is the letterbox fire-rated?", value: "N/A", type: "radio", options: ["Yes", "No", "N/A"], dependsOn: { key: "letterbox_present", value: "Yes" } },
  { key: "letterbox_operates_correctly", question: "Does the letterbox operate correctly?", value: "N/A", type: "radio", options: ["Yes", "No", "N/A"], dependsOn: { key: "letterbox_fire_rated", value: "Yes" } },
  { key: "hold_open_release_working", question: "Auto-door hold-open release mechanism working?", value: "N/A", type: "radio", options: ["Yes", "No", "N/A"] },
];

export const QUESTION_TEXT = Object.fromEntries(INITIAL_CHECKLIST.map((i) => [i.key, i.question]));

// ── 2. Gap-measurement grids ─────────────────────────────────────────
export const SINGLE_GAP_FIELDS = [
  { key: "top_hinge", label: "Top — hinge side" },
  { key: "top_mid", label: "Top — middle" },
  { key: "top_lock", label: "Top — lock side" },
  { key: "hinge_top", label: "Hinge side — top" },
  { key: "hinge_mid", label: "Hinge side — middle" },
  { key: "hinge_bot", label: "Hinge side — bottom" },
  { key: "lock_top", label: "Lock side — top" },
  { key: "lock_mid", label: "Lock side — middle" },
  { key: "lock_bot", label: "Lock side — bottom" },
  { key: "bot_hinge", label: "Bottom — hinge side" },
  { key: "bot_mid", label: "Bottom — middle" },
  { key: "bot_lock", label: "Bottom — lock side" },
];

export const DOUBLE_GAP_FIELDS = [
  { key: "d2_top_lock", label: "2nd leaf top — lock side" },
  { key: "d2_top_mid", label: "2nd leaf top — middle" },
  { key: "d2_top_hinge", label: "2nd leaf top — hinge side" },
  { key: "d2_hinge_top", label: "2nd leaf hinge — top" },
  { key: "d2_hinge_mid", label: "2nd leaf hinge — middle" },
  { key: "d2_hinge_bot", label: "2nd leaf hinge — bottom" },
  { key: "d2_bot_hinge", label: "2nd leaf bottom — hinge side" },
  { key: "d2_bot_mid", label: "2nd leaf bottom — middle" },
  { key: "d2_bot_lock", label: "2nd leaf bottom — lock side" },
];

export const THRESHOLD_GAP_KEYS = ["bot_lock", "bot_mid", "bot_hinge", "d2_bot_lock", "d2_bot_mid", "d2_bot_hinge"];
export const FIRE_RATINGS = ["FD30", "FD60", "FD90", "FD120", "Unknown"];

export const gapIsFail = (v) => {
  const n = parseFloat(v);
  return !isNaN(n) && n > 4;
};

export function isItemFailed(item) {
  if (item.isGate) return false;
  if (item.type === "switch") return item.value === false;
  if (item.type === "radio") return item.value === "No";
  return false;
}

// Which checklist items are currently visible (letterbox cascade).
export function visibleItems(checklist) {
  const map = Object.fromEntries(checklist.map((i) => [i.key, i.value]));
  return checklist.filter((it) => {
    if (!it.dependsOn) return true;
    return map[it.dependsOn.key] === it.dependsOn.value;
  });
}

// ── 15. Status calculation ───────────────────────────────────────────
export function calculateStatus({ checklist, gaps, gapWithinTolerance, thresholdGapWithinTolerance, noAccess, isFireDoor }) {
  if (noAccess) return "no_access";
  const CRITICAL_KEYS = ["door_label_present", "door_frame_secure", "smoke_fire_seals_intact"];
  if (!isFireDoor) return "fail";
  if (checklist.some((it) => CRITICAL_KEYS.includes(it.key) && (it.value === false || it.value === "No"))) return "fail";
  const EDGE = ["lock_top", "lock_mid", "lock_bot", "hinge_bot", "hinge_mid", "hinge_top", "top_hinge", "top_mid", "top_lock", "d2_top_lock", "d2_top_mid", "d2_top_hinge", "d2_hinge_top", "d2_hinge_mid", "d2_hinge_bot"];
  const BOT = ["bot_lock", "bot_mid", "bot_hinge", "d2_bot_hinge", "d2_bot_mid", "d2_bot_lock"];
  if (EDGE.some((k) => parseFloat(gaps[k] || "0") > 4)) return "fail";
  if (BOT.some((k) => parseFloat(gaps[k] || "0") > 4)) return "fail";
  if (!gapWithinTolerance) return "fail";
  if (!thresholdGapWithinTolerance) return "fail";
  const anyOtherFail = checklist.some((it) => !CRITICAL_KEYS.includes(it.key) && !it.isGate && (it.value === false || it.value === "No"));
  if (anyOtherFail) return "review";
  return "pass";
}

// Map form gap keys → legacy flat ChecklistData fields (best-effort).
function legacyGaps(gaps) {
  const n = (k) => { const v = parseFloat(gaps[k]); return isNaN(v) ? null : v; };
  return {
    gap_top_left: n("top_hinge"), gap_top_center: n("top_mid"), gap_top_right: n("top_lock"),
    gap_hinge_top: n("hinge_top"), gap_hinge_center: n("hinge_mid"), gap_hinge_bottom: n("hinge_bot"),
    gap_latch_top: n("lock_top"), gap_latch_center: n("lock_mid"), gap_latch_bottom: n("lock_bot"),
    gap_under_hinge: n("bot_hinge"), gap_under_center: n("bot_mid"), gap_under_lock: n("bot_lock"),
  };
}

// Double-door leaf choice for a failed component (matches mobile Iter-188).
export const LEAF_OPTIONS = [
  { key: "leaf1", label: "Leaf 1", hint: "opening leaf" },
  { key: "leaf2", label: "Leaf 2", hint: "fixed leaf" },
  { key: "both", label: "Both leaves", hint: "quoted ×2" },
];

// Informational bullets that must NOT be treated as priceable reasons on
// hydration (letterbox meta / measurements). Mirrors the backend
// parse_reason_bullets META filter.
const META_BULLET_RE = /^(size of opening|reason for failure|perimeter|threshold|gap|measurement)\b/i;

// Extract the inspector's ticked REASONS from a FailedComponentPhoto
// notes blob (lines beginning with "•", excluding meta bullets). Used to
// rehydrate the reason tickboxes on edit for legacy rows with no
// explicit `reasons` array.
export function parseReasonBullets(notes) {
  const out = [];
  String(notes || "").split("\n").forEach((raw) => {
    const line = raw.trim();
    if (!line.startsWith("•")) return;
    const text = line.replace(/^•\s*/, "").trim();
    if (!text || META_BULLET_RE.test(text)) return;
    out.push(text);
  });
  return out;
}

// ── 16a. Build the POST /api/inspections payload from form state ─────
// `overrideStatus` (optional) lets the inspector force the overall
// outcome — "pass" / "fail" / "review" / "pass_with_observations" —
// instead of the auto-calculated one. Mirrors the mobile form's manual
// override buttons. `no_access` always wins when set.
export function buildInspectionPayload(state, doorId, overrideStatus) {
  const {
    checklist, spyViewerFireRated, gaps, gapWithinTolerance, thresholdGapWithinTolerance,
    doorLeafType, isFireDoor, fireRating, letterboxOpeningSize, letterboxFailureReason,
    failures, failureReasons = {}, failureOther = {}, customReasons = {}, failureLeaf = {},
    labelPhoto, photos, additionalObservations, notes, quoteNotes,
    signature, inspectorName, inspectionDate, noAccess, noAccessNote,
  } = state;

  const ans = Object.fromEntries(checklist.map((i) => [i.key, i.value]));
  const dropdownSealWorks = ans.dropdown_seal === "Yes";

  // Only persist gap keys for the active leaf configuration.
  const activeKeys = [...SINGLE_GAP_FIELDS, ...(doorLeafType === "double" ? DOUBLE_GAP_FIELDS : [])].map((f) => f.key);
  const gapMap = {};
  activeKeys.forEach((k) => { if (gaps[k] !== undefined && gaps[k] !== "") gapMap[k] = String(gaps[k]); });

  const recs = [
    ...recommendGapRemedies(gapMap, { skipUnderDoor: dropdownSealWorks }),
    ...(doorLeafType === "double" ? recommendGapRemedies(gapMap, { keyPrefix: "d2_", leafLabel: "Second leaf", skipUnderDoor: dropdownSealWorks }) : []),
  ];

  const autoStatus = noAccess ? "no_access" : calculateStatus({ checklist, gaps, gapWithinTolerance, thresholdGapWithinTolerance, noAccess, isFireDoor });
  // Manual override wins over the auto classification (except no access).
  const finalStatus = noAccess ? "no_access" : (overrideStatus || autoStatus);

  const checklist_data = {
    door_label_present: ans.door_label_present === true,
    is_fire_door: isFireDoor,
    hinge_condition: ans.three_hinges_fitted === false ? "Failed" : "Good",
    lock_condition: ans.handle_lock_secure === false ? "Failed" : "Good",
    smoke_seals_condition: ans.smoke_fire_seals_intact === false ? "Failed" : "Good",
    letterbox_present: ans.letterbox_present,
    letterbox_fire_rated: ans.letterbox_fire_rated,
    letterbox_operates_correctly: ans.letterbox_operates_correctly,
    letterbox_opening_size: letterboxOpeningSize || "",
    letterbox_failure_reason: letterboxFailureReason || "",
    hold_open_release_working: ans.hold_open_release_working,
    spy_viewer_fire_rated: ans.spy_viewer === "Yes" ? spyViewerFireRated : "N/A",
    gap_measurements: gapMap,
    gap_within_tolerance: gapWithinTolerance,
    threshold_gap_within_tolerance: dropdownSealWorks ? true : thresholdGapWithinTolerance,
    threshold_gap_reasons: [],
    threshold_gap_bypassed_by_seal: dropdownSealWorks,
    gap_recommendations: recs,
    door_leaf_type: doorLeafType,
    fire_rating: fireRating || "Unknown",
    additional_observations: additionalObservations || "",
    no_access: !!noAccess,
    no_access_note: noAccessNote || "",
    custom_items: ans,
    ...legacyGaps(gaps),
  };

  const isDouble = doorLeafType === "double";
  const failedList = checklist.filter((it) => isItemFailed(it));

  // Each failed component carries its ticked reasons as "• reason"
  // bullets in `notes` (the backend prices from these via
  // parse_reason_bullets), plus letterbox meta as non-priceable info
  // bullets, plus the double-door leaf choice.
  const failed_component_photos = failedList.map((it) => {
    const f = failures[it.key] || {};
    const ph = f.photos || [];
    const reasons = failureReasons[it.key] || [];
    const other = (failureOther[it.key] || "").trim();
    const noteParts = [];
    reasons.forEach((r) => noteParts.push(`• ${r}`));
    if (other && !reasons.some((r) => r.toLowerCase() === other.toLowerCase())) noteParts.push(`• ${other}`);
    if (it.key === "letterbox_operates_correctly") {
      if ((letterboxOpeningSize || "").trim()) noteParts.push(`• Size of opening: ${letterboxOpeningSize.trim()}`);
      if ((letterboxFailureReason || "").trim()) noteParts.push(`• Reason for failure: ${letterboxFailureReason.trim()}`);
    }
    const entry = {
      key: it.key,
      question: it.question,
      photo: ph[0] || "",
      photos: ph,
      notes: noteParts.join("\n"),
      reasons,
    };
    if (isDouble) entry.leaf = failureLeaf[it.key] || "both";
    return entry;
  });

  // Custom "add your own answer" entries the inspector added AND ticked
  // (plus any text still sitting in the box) → backend promotes each to
  // a Price Log awaiting-price row.
  const custom_failure_answers = [];
  failedList.forEach((it) => {
    const ticked = failureReasons[it.key] || [];
    (customReasons[it.key] || []).forEach((r) => {
      if (ticked.includes(r)) custom_failure_answers.push({ key: it.key, question: it.question, text: r });
    });
    const pending = (failureOther[it.key] || "").trim();
    if (pending && !custom_failure_answers.some((e) => e.key === it.key && e.text.toLowerCase() === pending.toLowerCase())) {
      custom_failure_answers.push({ key: it.key, question: it.question, text: pending });
    }
  });

  // Backend enum doesn't accept `pass_with_observations` on the wire —
  // send `pass` and stash the real intent in checklist_data so the
  // report layer (effectiveStatus) re-derives PWO. Mirrors mobile.
  let wireStatus = finalStatus;
  if (finalStatus === "pass_with_observations") {
    wireStatus = "pass";
    checklist_data.intended_effective_status = "pass_with_observations";
  }

  return {
    door_id: doorId,
    inspection_date: inspectionDate
      ? new Date(`${String(inspectionDate).slice(0, 10)}T12:00:00`).toISOString()
      : new Date().toISOString(),
    status: wireStatus,
    checklist_data,
    photos: photos || [],
    label_photo: labelPhoto || null,
    failed_component_photos,
    custom_failure_answers,
    notes: notes || "",
    quote_notes: quoteNotes || null,
    synced: true,
    signature: signature || inspectorName || null,
  };
}
